import {StyledChatBoxContainer, StyledDivContainer} from '../AskGPViewsStyles'
import {useCallback, useEffect, useRef, useState} from 'react'
import {StyledBackdrop} from './ChatViewStyles'
import {setFilters, setSearchQuery, setSearchTermQuery} from '../../../../../store/askGivePrism/askGivePrismSlice'
import {
  getAllProspectsData,
  setSortColumn,
  setSortConfig,
  setSortDirection
} from '../../../../../store/prospectSegment/allProspectsData/allProspectsDataSlice'
import {getProspectsPageNumber} from '../../../../../store/pagination/pageNumberSlice'
import {selectedTab} from '../../../../../store/prospectSegment/selectedProspectsTab/selectedProspectsTabSlice'
import {useDispatch, useSelector} from 'react-redux'
import {
  setCancellation,
  setChatbotData,
  setInterestTagId,
  setLatestResponseId,
  setLLMLoader,
  setMatchedProspectId,
  setMessage,
  setShouldResetChatView
} from '../../../../../store/askGivePrism/chatbot/chatbotStreamSlice'
import {useIsAuthenticated} from '@azure/msal-react'
import ConfirmDialog from './components/ConfirmDialog'
import WelcomeTiles from './components/WelcomeTiles'
import ChatInput from './components/ChatInput'
import ChatPromptArea from './components/ChatPromptArea'
import ChatMessages from './components/ChatMessages'
import {extractProspectAndInterestIds} from './helper'
import useSessionManager from '../../../../../hooks/useSessionManager'
import {setResetSearchTerm, updatedAllProspectsSearchTerm} from '../../../../../store/search/searchSlice'

const ChatViewIndex = () => {
  const sendButtonRef = useRef(null)
  const textFieldRef = useRef(null)
  const [searchWord, setSearchWord] = useState('')
  const [mainPrompt, setMainPrompt] = useState('')
  const [editedPrompt, setEditedPrompt] = useState('')

  const [popupTitle, setPopupTitle] = useState('')
  const [popupContent, setPopupContent] = useState('')
  const [openPopup, setOpenPopup] = useState(false)

  const [searchClicked, setSearchClicked] = useState(false)
  const [editClicked, setEditClicked] = useState(false)
  const [clearIconClicked, setClearIconClicked] = useState(false)
  const [sendIconClicked, setSendIconClicked] = useState(false)

  const [responseData, setResponseData] = useState([])
  const dispatch = useDispatch()
  const [loader, setLoader] = useState(false)
  const refData = useSelector((state) => state.opportunityBank.refData)
  const [interestTagRefData, setInterestTagRefData] = useState(null)

  const matchedProspectIdLLM = useSelector((state) => state.chatBotSlice.matchedProspectId)
  const interestTagIdLLM = useSelector((state) => state.chatBotSlice.interestTagId)

  const isAuthenticated = useIsAuthenticated()

  const [sessionResponseId, setSessionResponseId] = useState(null)
  const {createSession, deleteSession, sendMessage, cancelResponse, cancelProcess} = useSessionManager()
  const [error, setError] = useState(false)

  const [receivedId, setReceivedId] = useState(false)
  const messagesEndRef = useRef(null)

  const responseId = useSelector((state) => state.chatBotSlice.latestResponseId)
  const cancelResponseState = useSelector((state) => state.chatBotSlice.isCancelled)
  const chatbotData = useSelector((state) => state.chatBotSlice.chatbotData)
  const shouldResetChatView = useSelector((state) => state.chatBotSlice.shouldResetChatView)

  const [agentName, setAgentName] = useState('')

  useEffect(() => {
    if (refData) {
      setInterestTagRefData(refData.find((obj) => obj.filter === 'interest_tag'))
    }
  }, [refData])

  useEffect(() => {
    if (shouldResetChatView) {
      setSessionResponseId(null)
      setResponseData(chatbotData)
      setMainPrompt('')
      setSearchWord('')
      setEditedPrompt('')
      setSearchClicked(false)
      setLoader(false)
      dispatch(setShouldResetChatView(false))
    }
  }, [shouldResetChatView, dispatch, chatbotData])

  useEffect(() => {
    dispatch(setMessage(mainPrompt))
  }, [mainPrompt, dispatch])

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({behavior: 'smooth', block: 'nearest'})
    }
    if (responseData.length > 0 && !loader) {
      dispatch(setChatbotData(responseData))
    }
    if (responseData.length === 0) {
      setResponseData(chatbotData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseData, dispatch, loader])

  useEffect(() => {
    if (sendButtonRef.current) {
      sendButtonRef.current.focus()
    }
  }, [responseData])

  useEffect(() => {
    dispatch(setLLMLoader(loader))
  }, [loader, dispatch])

  useEffect(() => {
    if ((matchedProspectIdLLM.length > 0 || interestTagIdLLM.length > 0) && !loader && !error && receivedId) {
      dispatch(
        setFilters({
          uniqueConstituentIdentifier: matchedProspectIdLLM,
          tagId: interestTagIdLLM
        })
      )
      setReceivedId(false)
    }
    dispatch(getProspectsPageNumber(1))
    dispatch(setSortColumn(null))
    dispatch(setSortDirection('asc'))
    dispatch(setSortConfig({direction: 'asc', key: null}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedProspectIdLLM, dispatch, interestTagIdLLM, loader, error])

  function initializeSearchState(sessionId) {
    const localEditedPrompt = editedPrompt
    setSearchClicked(true)
    setLoader(true)
    setEditedPrompt(localEditedPrompt)
    setSearchWord('')
    if (sendIconClicked && editClicked) {
      setSendIconClicked(false)
      setEditClicked(false)
      setMainPrompt(editedPrompt)
    } else if (!mainPrompt && !sessionId) {
      setMainPrompt(searchWord)
    } else if (mainPrompt && !sessionId) {
      setResponseData((prevPrompts) => [...prevPrompts, {sender: 'user', data: {text: searchWord}}])
    }
  }

  // Updated main handleStream function that calls both functions
  async function handleStream(sessionId) {
    const messageStream = await initializeStream(sessionId)

    if (messageStream) {
      await processStreamData(messageStream)
    }
  }

  async function initializeStream(sessionId) {
    try {
      const messageStream = await sendMessage(sessionId, searchWord || editedPrompt)

      // Check if the request was cancelled before we got the stream
      if (!messageStream) {
        setLoader(false)
        return null
      }

      return messageStream
    } catch (error) {
      if (error.name === 'AbortError') {
        setLoader(false)
        return null
      }
      throw error
    }
  }

  // Function 2: Process the stream data
  async function processStreamData(messageStream) {
    const reader = messageStream.getReader()
    const decoder = new TextDecoder('utf-8')
    let buffer = ''

    while (true) {
      const {done, value} = await reader.read()
      if (done) {
        setLoader(false)
        break
      }

      const chunk = decoder.decode(value)
      buffer += chunk

      // Process complete data events from the buffer
      const lines = buffer.split('\n')
      // Keep the last line which might be incomplete
      buffer = lines.pop() || ''

      // Process each complete line
      for (const line of lines) {
        if (line.trim() && line.startsWith('data: ')) {
          try {
            processStreamChunk(line)
          } catch (error) {
            setError(true)
            setLoader(false)
            console.error('Error processing chunk:', error)
          }
        }
      }
    }
  }

  const handleSearch = useCallback(async () => {
    dispatch(setLatestResponseId(null))
    setError(false)
    setAgentName('askGivePrismAgent')
    let sessionId = sessionResponseId || null
    initializeSearchState(sessionId)
    if (sessionId) {
      setResponseData((prevPrompts) => [...prevPrompts, {sender: 'user', data: {text: searchWord}}])
    } else {
      try {
        const sessionDataId = await createSession()
        setSessionResponseId(sessionDataId)
        sessionId = sessionDataId
      } catch (error) {
        console.error('Error:', error)
        setLoader(false)
        setError(true)
        return {
          text: 'Error occurred. Please try again later.',
          matchedProspectId: [],
          interestTagId: [],
          interestTags: []
        }
      }
    }

    if (sessionId && isAuthenticated) {
      try {
        await handleStream(sessionId)
      } catch (error) {
        console.error('Stream error:', error)
        handleErrorResponse('An Error occurred. Please try again later.')
      }
    } else {
      handleErrorResponse('You are not authenticated. Please login to continue.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionResponseId, searchWord, editedPrompt, mainPrompt, createSession])

  function handleErrorResponse(errorMessage) {
    setLoader(false)
    setError(true)
    setResponseData((prevResponse) => [
      ...prevResponse,
      {
        sender: 'bot',
        data: {
          text: errorMessage,
          agent: 'askGivePrismAgent',
          matchedProspectId: [],
          interestTagId: [],
          interestTags: []
        }
      }
    ])
  }

  // Helper function to process stream chunks
  function processStreamChunk(chunk) {
    if (cancelResponseState) {
      return // Don't process any more chunks if cancelled
    }

    // Remove data: prefix if present
    const jsonString = chunk.replace(/^data: /, '').trim()
    if (!jsonString) return

    try {
      // First attempt: try parsing the entire JSON
      const parsedData = JSON.parse(jsonString)

      if (parsedData?.error || parsedData?.error === '' || parsedData?.errorCode || parsedData?.errorCode === '') {
        handleErrorResponse('An Error occurred in response. Please try again later.')
        return
      }

      // Extract the response id. Used while cancelling the response
      const requestId = parsedData?.event === 'request_started' ? parsedData?.request_id : null
      if (requestId) {
        dispatch(setLatestResponseId(requestId))
      }

      // Directly extract cancellation request if available
      const cancellationRequest = parsedData?.event === 'cancelled' ? parsedData?.message : null
      if (cancellationRequest) {
        dispatch(setCancellation(false))
        dispatch(setLatestResponseId(null))
        setLoader(false)
        setResponseData((prevResponse) => [
          ...prevResponse,
          {
            sender: 'bot',
            data: {
              text: 'Ok, ready for your next prompt.',
              agent: 'askGivePrismAgent',
              matchedProspectId: [],
              interestTagId: [],
              interestTags: []
            }
          }
        ])
        return
      }

      if (parsedData?.content?.parts?.[0]?.functionResponse?.name === 'EngagementAgent') {
        setAgentName(parsedData?.content?.parts?.[0]?.functionResponse?.name)
      }
      const isPartial = parsedData?.partial === true

      if (!isPartial) {
        // This is the final complete message - process it normally
        processExecutionResults(parsedData)
      }
    } catch (error) {
      console.error('Failed to parse stream chunk:', error)
      if (jsonString.includes('"error"') || jsonString.includes('"error_code"')) {
        handleErrorResponse('An Error occurred in response. Please try again later.')
      }
    }
  }

  // Helper function to process execution results data
  function processExecutionResults(parsedData) {
    // Extract the required fields
    const {prospectIds, interestIds} = extractProspectAndInterestIds(parsedData?.content?.parts?.[0]?.text || '')
    const formattedText = parsedData?.content?.parts?.[0]?.text || ''
    if (parsedData?.content?.parts?.[0]?.functionResponse?.name === 'EngagementAgent') {
      setAgentName(parsedData?.content?.parts?.[0]?.functionResponse?.name)
    }
    const matchedProspectId =
      parsedData?.content?.parts?.[0]?.functionResponse?.response?.matched_prospect_id?.map((id) => id.toString()) ||
      prospectIds ||
      []
    const interestTagId =
      parsedData?.content?.parts?.[0]?.functionResponse?.response?.interest_tags || interestIds || []
    const filterSetInterestTagId = new Set(interestTagId)

    if (matchedProspectId.length > 0) {
      setReceivedId(true)
      dispatch(setMatchedProspectId(matchedProspectId))
    }
    if (interestTagId.length > 0) {
      setReceivedId(true)
      dispatch(setInterestTagId(interestTagId))
    }

    if (formattedText !== '' || matchedProspectId.length > 0 || interestTagId.length > 0) {
      // Format the response object
      const formattedResponse = {
        text: formattedText,
        agent: agentName,
        matchedProspectId,
        matchedProspectsList: [],
        interestTagId,
        interestTags: interestTagRefData?.valuesWithId.filter((item) => filterSetInterestTagId.has(item.id)) || []
      }

      // Update the state with the formatted response
      setResponseData((prevResponse) => [...prevResponse, {sender: 'bot', data: formattedResponse}])
    }
  }

  useEffect(() => {
    if (!sessionResponseId && sendIconClicked) {
      handleSearch()
    }
  }, [sessionResponseId, sendIconClicked, handleSearch])

  const handleEditClick = () => {
    setEditClicked(true)
    setEditedPrompt(mainPrompt)
    setTimeout(() => {
      textFieldRef.current?.focus()
    }, 0)
  }

  const handleModalClose = () => {
    setOpenPopup(false)
    setClearIconClicked(false)
    setSendIconClicked(false)
  }

  const handleConfirm = async () => {
    setOpenPopup(false)
    if (clearIconClicked) {
      setSessionResponseId(null)
      setMainPrompt('')
      setSearchWord('')
      setEditedPrompt('')
      setSearchClicked(false)
      setClearIconClicked(false)
      dispatch(setFilters({}))
      dispatch(getAllProspectsData([]))
      dispatch(getProspectsPageNumber(1))
      dispatch(selectedTab('allProspects'))
      setResponseData([])
      dispatch(setChatbotData([]))
      dispatch(setMatchedProspectId([]))
      dispatch(setInterestTagId([]))
      // reset search terms
      dispatch(setSearchTermQuery({previousTerm: '', currentTerm: ''})) // reset ask give prism chatbot search term
      dispatch(setSearchQuery('')) // reset ask give prism chatbot search query
      dispatch(updatedAllProspectsSearchTerm({currentTerm: '', previousTerm: ''})) // reset all prospects search term
      dispatch(setResetSearchTerm(true)) // reset search term
      dispatch(setSortColumn(null))
      dispatch(setSortDirection('asc'))
      dispatch(setSortConfig({direction: 'asc', key: null}))
      await handleClearSession()
    } else if (sendIconClicked) {
      await handleClearSession()
      setSessionResponseId(null)
      setMainPrompt(editedPrompt)
      setSearchWord('')
      setResponseData([])
      dispatch(setChatbotData([]))
      dispatch(setFilters({}))
      dispatch(getAllProspectsData([]))
      dispatch(getProspectsPageNumber(1))
      dispatch(selectedTab('allProspects'))
      dispatch(setMatchedProspectId([]))
      dispatch(setInterestTagId([]))
      dispatch(setResetSearchTerm(true)) // reset search term
      dispatch(setSearchTermQuery({previousTerm: '', currentTerm: ''})) // reset ask give prism chatbot search term
      dispatch(setSearchQuery('')) // reset ask give prism chatbot search query
      dispatch(updatedAllProspectsSearchTerm({currentTerm: '', previousTerm: ''})) // reset all prospects search term
      dispatch(setSortColumn(null))
      dispatch(setSortDirection('asc'))
      dispatch(setSortConfig({direction: 'asc', key: null}))
    } else {
      setEditClicked(false)
      setEditedPrompt(mainPrompt)
    }
  }

  const handleClearSession = async () => {
    await deleteSession(sessionResponseId)
    setSessionResponseId(null)
    return true
  }

  const handleClearClick = () => {
    setClearIconClicked(true)
    setOpenPopup(true)
    setPopupTitle('Ready for a new chat?')
    setPopupContent('You are about to clear your chat history and start over. Are you sure you want to continue?')
  }

  const handleCrossClick = () => {
    if (editedPrompt.trim() === mainPrompt.trim()) {
      setEditClicked(false)
      setEditedPrompt(mainPrompt)
      return
    }
    setOpenPopup(true)
    setPopupTitle('Erase your prompt?')
    setPopupContent('You will lose your chat history by erasing this prompt. Are you sure you want to continue?')
  }

  const handleSendClick = () => {
    setSendIconClicked(true)
    setOpenPopup(true)
    setPopupTitle('Change prompt?')
    setPopupContent('This would reset all the chat history and the prompt. Are you sure you want continue?')
  }

  const handleCancelResponse = async () => {
    setResponseData((prevResponse) => [
      ...prevResponse,
      {
        sender: 'bot',
        data: {
          text: "Got it, I'll stop my response.",
          agent: 'askGivePrismAgent',
          matchedProspectId: [],
          interestTagId: [],
          interestTags: []
        }
      }
    ])
    dispatch(setCancellation(true))
    if (responseId) {
      try {
        await cancelResponse(responseId)
      } catch (error) {
        console.warn('Server-side cancellation failed, using client-side:', error)
      }
    } else {
      cancelProcess()
      setLoader(false)
      dispatch(setLatestResponseId(null))
      dispatch(setCancellation(false))
      setResponseData((prevResponse) => [
        ...prevResponse,
        {
          sender: 'bot',
          data: {
            text: 'Ok, ready for your next prompt.',
            agent: 'askGivePrismAgent',
            matchedProspectId: [],
            interestTagId: [],
            interestTags: []
          }
        }
      ])
    }
  }

  return (
    <>
      <StyledChatBoxContainer>
        {!searchClicked && <WelcomeTiles />}
        <StyledBackdrop open={editClicked} />
        {searchClicked && (
          <StyledDivContainer mainPrompt={!!mainPrompt}>
            <ChatPromptArea
              mainPrompt={mainPrompt}
              editedPrompt={editedPrompt}
              editClicked={editClicked}
              handleEditClick={handleEditClick}
              handleClearClick={handleClearClick}
              handleCrossClick={handleCrossClick}
              handleSendClick={handleSendClick}
              textFieldRef={textFieldRef}
              setEditedPrompt={setEditedPrompt}
              loader={loader}
            />
            <ChatMessages responseData={responseData} loader={loader} />
            <div ref={messagesEndRef} />
          </StyledDivContainer>
        )}

        <ChatInput
          loader={loader}
          searchWord={searchWord}
          setSearchWord={setSearchWord}
          handleSearch={handleSearch}
          sendButtonRef={sendButtonRef}
          showDisclaimer={!!mainPrompt}
          handleCancelResponse={handleCancelResponse}
          responseId={responseId}
          cancelResponseState={cancelResponseState}
          handleNewChat={handleClearClick}
          responseData={responseData}
          agent={agentName}
        />
      </StyledChatBoxContainer>

      <ConfirmDialog
        open={openPopup}
        title={popupTitle}
        content={popupContent}
        handleClose={handleModalClose}
        handleConfirm={handleConfirm}
      />
    </>
  )
}

export default ChatViewIndex
